// getCategoryFeedsHasMessages.js
'use strict';
import { Map } from 'immutable';

import createCachedSelector from '../resource/createCachedSelector.js';
import { createSelectorCreator, defaultMemoize } from 'reselect';
import { sanitizeFeedQuery } from '../resource/feedUtils.js';
import { FeedNameType } from '../resource/feedConstants.js';

const shallowEqualObject = (prev, next) => {
  if (prev === next) return true;
  if (!prev || !next) return false;
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  if (prevKeys.length !== nextKeys.length) return false;
  return prevKeys.every(k => prev[k] === next[k]);
};

// Second-layer guard: keeps the previous result reference when a relevant list
// update leaves all per-category boolean flags unchanged.
const createShallowResultSelector = createSelectorCreator(defaultMemoize, {
  resultEqualityCheck: shallowEqualObject,
});

const defaultMap = new Map();

/**
 * Select per-category flag indicating whether the feed has messages.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {array} categories - category feed names.
 * @return {Object} Map of category to undefined | boolean — undefined means not yet fetched,
 *   false means fetched with 0 items, true means fetched with at least one item.
 */
const getCategoryFeedsHasMessages = createCachedSelector(
  state => state.getIn(['lists', 'home', FeedNameType.MESSAGES]) || defaultMap,
  (state, categories) => categories,
  (messageFeeds, categories) =>
    categories.reduce((categoryFeeds, category) => {
      const itemIds = messageFeeds.getIn([
        sanitizeFeedQuery({ feedName: category }),
        'itemIds',
      ]);

      categoryFeeds[category] =
        itemIds === null || itemIds === undefined
          ? undefined
          : itemIds.size > 0;
      return categoryFeeds;
    }, {})
)({
  keySelector: (state, categories) => `${categories}`,
  selectorCreator: createShallowResultSelector,
});

export default getCategoryFeedsHasMessages;
